/**
 * /logistica/cargas/:id — Detalhe da Carga.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiMapPin,
  FiPlus,
  FiRefreshCw,
  FiSend,
  FiTrash2,
  FiTruck,
} from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  cancelarCarga,
  carregarOe,
  concluirCarga,
  iniciarCarregamento,
  listarOes,
  obterCarga,
  otimizarRota,
  ROTA_PROVIDER_LABEL,
  sairEmRota,
  SITUACAO_CARGA_LABELS,
  SITUACAO_CARGA_TONES,
  SITUACAO_OE_LABELS,
  SITUACAO_OE_TONES,
} from "../../../services/logistica";
import { listarLocais } from "../../../services/empresa";
import { PageContainer } from "../../../styles/shared";

import { fmtData } from "../../Orcamentos/helpers";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Pill,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
  Textarea,
} from "../../Orcamentos/styles";


// B8: formata duracao em segundos -> "X min" / "Yh Zmin".
function fmtDuracao(segundos) {
  const m = Math.round((segundos || 0) / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${m % 60}min`;
}

export default function CargaDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();

  const [carga, setCarga] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmando, setConfirmando] = useState(null);
  const [motivo, setMotivo] = useState("");
  // Vínculo OE -> Carga (auditoria 2026-08-07): a tela mandava "criar OEs em
  // /logistica/entregas vinculando a esta carga", mas o controle não existia
  // em lugar nenhum — a carga nascia e morria vazia.
  const [oesPendentes, setOesPendentes] = useState([]);
  const [oeParaVincular, setOeParaVincular] = useState("");
  const [origemEndereco, setOrigemEndereco] = useState("");
  // Locais da empresa (Minha empresa): o ponto de partida do caminhão vem
  // preenchido com o local marcado como saída das entregas.
  const [locais, setLocais] = useState([]);

  useEffect(() => {
    if (confirmando !== "otimizar") return undefined;
    let vivo = true;
    listarLocais(request, { ativos: true })
      .then((dados) => {
        if (!vivo) return;
        const lista = (dados?.locais || []).filter((l) => l.endereco_completo);
        setLocais(lista);
        const saida = lista.find((l) => l.saida_das_entregas);
        if (saida) setOrigemEndereco((atual) => atual || saida.endereco_completo);
      })
      .catch(() => { if (vivo) setLocais([]); });
    return () => { vivo = false; };
  }, [confirmando, request]);

  const podeCarregar = isMaster || hasAllPermissions(["logistica:carregar"]);
  const podePlanejar = isMaster || hasAllPermissions(["logistica:planejar"]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await obterCarga(request, id);
      setCarga(data);
      // OEs livres para embarcar nesta carga (só PENDENTE sem carga).
      try {
        // `sem_carga` filtra no servidor: antes a tela baixava todas as
        // OEs pendentes do tenant para filtrar no cliente.
        const p = await listarOes(request, { situacao: "PENDENTE", semCarga: true });
        setOesPendentes(Array.isArray(p) ? p : p?.results || []);
      } catch {
        setOesPendentes([]);
      }
    } catch (err) {
      setError(err?.message || "Erro ao carregar carga.");
    } finally {
      setLoading(false);
    }
  }, [request, id]);

  const vincularOe = async () => {
    if (!oeParaVincular) return;
    await carregarOe(request, oeParaVincular, { carga_id: id });
    setOeParaVincular("");
    await carregar();
  };

  useEffect(() => {
    carregar();
  }, [carregar]);

  const actions = (() => {
    if (!carga) return [];
    const out = [
      {
        label: "Voltar",
        icon: <FiArrowLeft size={14} />,
        onClick: () => navigate("/logistica/cargas"),
      },
      {
        label: "Atualizar",
        icon: <FiRefreshCw size={14} />,
        onClick: carregar,
      },
    ];
    if (carga.situacao === "PLANEJADA" && podeCarregar) {
      out.push({
        label: "Iniciar carregamento",
        icon: <FiTruck size={14} />,
        onClick: () => setConfirmando("carregar"),
        primary: true,
      });
    }
    if (carga.situacao === "CARREGANDO" && podeCarregar) {
      out.push({
        label: "Sair em rota",
        icon: <FiSend size={14} />,
        onClick: () => setConfirmando("rota"),
        primary: true,
      });
    }
    if (carga.situacao === "EM_ROTA" && podeCarregar) {
      out.push({
        label: "Concluir carga",
        icon: <FiCheckCircle size={14} />,
        onClick: () => setConfirmando("concluir"),
      });
    }
    // B8 (ADR-0038): otimizar ordem de visita das entregas (precisa de OEs).
    if (
      ["PLANEJADA", "CARREGANDO", "EM_ROTA"].includes(carga.situacao)
      && podePlanejar
      && (carga.ordens_entrega_detalhadas?.length || 0) > 0
    ) {
      out.push({
        label: "Otimizar rota",
        icon: <FiMapPin size={14} />,
        onClick: () => setConfirmando("otimizar"),
      });
    }
    if (
      ["PLANEJADA", "CARREGANDO", "EM_ROTA"].includes(carga.situacao)
      && podePlanejar
    ) {
      out.push({
        label: "Cancelar",
        icon: <FiTrash2 size={14} />,
        onClick: () => setConfirmando("cancelar"),
      });
    }
    return out;
  })();

  const executarAcao = useCallback(async () => {
    if (!carga || !confirmando) return;
    let promise;
    if (confirmando === "carregar") promise = iniciarCarregamento(request, id);
    else if (confirmando === "rota") promise = sairEmRota(request, id);
    else if (confirmando === "concluir") promise = concluirCarga(request, id);
    else if (confirmando === "cancelar") {
      if (!motivo.trim()) throw new Error("Motivo é obrigatório.");
      promise = cancelarCarga(request, id, { motivo });
    }
    else if (confirmando === "otimizar") {
      promise = otimizarRota(request, id, { origem_endereco: origemEndereco });
    }
    if (promise) {
      const data = await promise;
      setCarga(data);
      setConfirmando(null);
      setMotivo("");
      setOrigemEndereco("");
    }
  }, [confirmando, motivo, origemEndereco, request, id, carga]);

  if (loading && !carga) {
    return <PageContainer><EmptyMsg>Carregando...</EmptyMsg></PageContainer>;
  }
  if (!carga) {
    return (
      <PageContainer>
        <EntityHeader
          title="Erro"
          actions={[{
            label: "Voltar",
            icon: <FiArrowLeft size={14} />,
            onClick: () => navigate("/logistica/cargas"),
          }]}
        />
        {error && <ErrorMsg>{error}</ErrorMsg>}
      </PageContainer>
    );
  }

  const kpis = [
    {
      label: "Data planejada",
      value: carga.data_planejada,
      tone: "neutral",
    },
    {
      label: "Veículo",
      value: carga.veiculo_placa,
      sub: carga.veiculo_descricao || "",
      tone: "neutral",
    },
    {
      label: "OEs",
      value: carga.ordens_entrega_detalhadas?.length || 0,
      tone: (carga.ordens_entrega_detalhadas?.length || 0) > 0 ? "info" : "warning",
    },
    {
      label: "Motorista",
      value: carga.motorista_nome,
      sub: carga.motorista_telefone || "",
      tone: "neutral",
    },
  ];

  return (
    <PageContainer>
      <EntityHeader
        title={carga.numero}
        subtitle={`Carga de ${carga.data_planejada}`}
        badges={[{
          label: SITUACAO_CARGA_LABELS[carga.situacao] || carga.situacao,
          tone: SITUACAO_CARGA_TONES[carga.situacao] || "neutral",
        }]}
        actions={actions}
      />

      <KpiStrip items={kpis} />

      {error && <ErrorMsg>{error}</ErrorMsg>}

      <Card>
        <SectionTitle>Dados</SectionTitle>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 14 }}>
          <div><strong>Numero:</strong> {carga.numero}</div>
          <div><strong>Veículo:</strong> {carga.veiculo_placa} ({carga.veiculo_descricao})</div>
          <div><strong>Motorista:</strong> {carga.motorista_nome}</div>
          {carga.ajudante_nome && <div><strong>Ajudante:</strong> {carga.ajudante_nome}</div>}
          <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "4px 0" }} />
          <div><strong>Criada em:</strong> {fmtData(carga.created_at)}</div>
          {carga.carregamento_iniciado_em && (
            <div><strong>Carregamento iniciado:</strong> {fmtData(carga.carregamento_iniciado_em)}</div>
          )}
          {carga.saiu_em_rota_em && (
            <div><strong>Saiu em rota:</strong> {fmtData(carga.saiu_em_rota_em)}</div>
          )}
          {carga.concluida_em && (
            <div><strong>Concluída em:</strong> {fmtData(carga.concluida_em)}</div>
          )}
          {carga.cancelada_em && (
            <>
              <div><strong>Cancelada em:</strong> {fmtData(carga.cancelada_em)}</div>
              <div><strong>Motivo:</strong> {carga.motivo_cancelamento}</div>
            </>
          )}
        </div>
      </Card>

      <Card>
        <SectionTitle>Ordens de entrega ({carga.ordens_entrega_detalhadas?.length || 0})</SectionTitle>
        {(!carga.ordens_entrega_detalhadas || carga.ordens_entrega_detalhadas.length === 0) ? (
          <EmptyMsg>
            Nenhuma OE vinculada — use o seletor abaixo para embarcar as
            entregas pendentes nesta carga.
          </EmptyMsg>
        ) : (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>OE</th>
                  <th>Pedido</th>
                  <th>Cliente</th>
                  <th>Endereço</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {carga.ordens_entrega_detalhadas.map((oe) => (
                  <tr
                    key={oe.id}
                    onClick={() => navigate(`/logistica/entregas/${oe.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <td><strong>{oe.numero}</strong></td>
                    <td>{oe.pedido_numero}</td>
                    <td>{oe.pedido_cliente_nome}</td>
                    <td style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={oe.endereco_entrega}>
                      {oe.endereco_entrega}
                    </td>
                    <td>
                      <Pill $tone={SITUACAO_OE_TONES[oe.situacao] || "neutral"}>
                        {SITUACAO_OE_LABELS[oe.situacao] || oe.situacao}
                      </Pill>
                      {/* CRM-38: assinou quem não estava entre os autorizados
                          do cliente e ninguém conferiu ainda. */}
                      {oe.assinante_autorizado === false && !oe.assinatura_conferida_em && (
                        <Pill $tone="warning" style={{ marginLeft: 6 }}>Assinante a conferir</Pill>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}

        {/* Embarcar entrega nesta carga — o elo que faltava entre a OE
            (criada pela produção ao carregar o ambiente) e a viagem. */}
        {podeCarregar && !["CONCLUIDA", "CANCELADA"].includes(carga.situacao) && (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 12, flexWrap: "wrap" }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 280, flex: 1 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>Adicionar entrega pendente</span>
              <Select
                value={oeParaVincular}
                onChange={(e) => setOeParaVincular(e.target.value)}
                disabled={!oesPendentes.length}
              >
                <option value="">
                  {oesPendentes.length
                    ? "— escolha a OE —"
                    : "Nenhuma OE pendente sem carga"}
                </option>
                {oesPendentes.map((oe) => (
                  <option key={oe.id} value={oe.id}>
                    {oe.numero} · {oe.pedido_numero} · {oe.pedido_cliente_nome || "cliente"}
                  </option>
                ))}
              </Select>
            </label>
            <AsyncButton
              variant="secondary"
              onClick={vincularOe}
              disabled={!oeParaVincular}
              successMessage="Entrega embarcada nesta carga."
            >
              <FiPlus size={14} /> Embarcar
            </AsyncButton>
          </div>
        )}
      </Card>

      {carga.rota_otimizada && (carga.rota_otimizada.paradas?.length || 0) > 0 && (
        <Card>
          <SectionTitle>
            Rota otimizada ({carga.rota_otimizada.paradas.length} paradas)
          </SectionTitle>
          {carga.rota_otimizada.aproximada ? (
            <div style={{
              background: "var(--warning-bg)", border: "1px solid var(--warning-border)",
              color: "var(--warning-text)", borderRadius: 8, padding: "8px 12px",
              fontSize: 13, marginBottom: 10,
            }}>
              Rota <strong>estimada</strong> (ordenada por proximidade aproximada).
              Distâncias reais em vias entram ativando um provedor de rota — grátis
              via OpenRouteService em Logística &rsaquo; Configuração.
            </div>
          ) : (
            <div style={{
              background: "var(--success-bg)", border: "1px solid var(--success-border)",
              color: "var(--success-text)", borderRadius: 8, padding: "8px 12px",
              fontSize: 13, marginBottom: 10,
            }}>
              Rota <strong>otimizada via {ROTA_PROVIDER_LABEL[carga.rota_otimizada.provider] || carga.rota_otimizada.provider}</strong>
              {" "}— distâncias e tempos calculados em vias reais.
            </div>
          )}
          <div style={{ display: "flex", gap: 18, fontSize: 13, marginBottom: 12, opacity: 0.85 }}>
            <span><strong>Distância total:</strong> {(carga.rota_otimizada.distancia_total_m / 1000).toFixed(1)} km</span>
            <span><strong>Duração estimada:</strong> {fmtDuracao(carga.rota_otimizada.duracao_total_s)}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {carga.rota_otimizada.origem?.endereco && (
              <div style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 13, opacity: 0.7, padding: "4px 0" }}>
                <span style={{
                  minWidth: 26, height: 26, borderRadius: "50%", display: "grid", placeItems: "center",
                  background: "var(--surface-2)", fontSize: 11, fontWeight: 700,
                }}>★</span>
                <span>Origem: {carga.rota_otimizada.origem.endereco || "—"}</span>
              </div>
            )}
            {carga.rota_otimizada.paradas.map((p) => (
              <div key={p.referencia} style={{
                display: "grid", gridTemplateColumns: "26px 1fr auto", gap: 10,
                alignItems: "center", padding: "6px 0", borderTop: "1px solid var(--divider)",
              }}>
                <span style={{
                  minWidth: 26, height: 26, borderRadius: "50%", display: "grid", placeItems: "center",
                  background: "var(--accent-soft)", color: "var(--accent)", fontSize: 12, fontWeight: 700,
                }}>{p.ordem}</span>
                <span style={{ fontSize: 13 }}>
                  {p.rotulo ? <strong>{p.rotulo} · </strong> : null}{p.endereco}
                </span>
                <span style={{ fontSize: 12, opacity: 0.65, whiteSpace: "nowrap" }}>
                  {(p.distancia_m / 1000).toFixed(1)} km · {fmtDuracao(p.duracao_s)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {confirmando && (
        <SlideOverPanel
          open
          onClose={() => { setConfirmando(null); setMotivo(""); }}
          title={({
            carregar: "Iniciar carregamento",
            rota: "Sair em rota",
            concluir: "Concluir carga",
            cancelar: "Cancelar carga",
            otimizar: "Otimizar rota",
          })[confirmando]}
          footer={
            <AsyncButton
              variant={confirmando === "cancelar" ? "danger" : "primary"}
              onClick={executarAcao}
              successMessage={`Ação ${confirmando} executada`}
            >
              Confirmar
            </AsyncButton>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {confirmando === "cancelar" && (
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Motivo *</span>
                <Textarea
                  rows={3}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Obrigatório - explique o motivo"
                  maxLength={300}
                />
              </label>
            )}
            {confirmando === "otimizar" && locais.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Sair de:</span>
                {locais.map((local) => (
                  <button
                    key={local.id}
                    type="button"
                    className="btn btn--ghost"
                    aria-pressed={origemEndereco === local.endereco_completo}
                    onClick={() => setOrigemEndereco(local.endereco_completo)}
                  >
                    {local.nome}
                  </button>
                ))}
              </div>
            )}
            {confirmando === "otimizar" && (
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Endereço de origem (opcional)</span>
                <Textarea
                  rows={2}
                  value={origemEndereco}
                  onChange={(e) => setOrigemEndereco(e.target.value)}
                  placeholder="Ex.: endereço da fábrica (ponto de partida do caminhão)"
                  maxLength={300}
                />
              </label>
            )}
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              {confirmando === "carregar" && "Marca carga como CARREGANDO. Todas as OEs PENDENTE vinculadas vão para CARREGADA."}
              {confirmando === "rota" && "Marca carga como EM_ROTA. Caminhao saiu da fabrica."}
              {confirmando === "concluir" && "Marca carga como CONCLUÍDA. Todas as OEs precisam estar com desfecho (ENTREGUE ou NAO_ENTREGUE)."}
              {confirmando === "cancelar" && "Aborta a carga. OEs CARREGADAS voltam pra PENDENTE."}
              {confirmando === "otimizar" && "Calcula a ordem de visita das entregas (do ponto de origem até a última parada). Por padrão usa OpenRouteService (grátis) com distâncias reais; cai pra estimativa por proximidade se nenhum provedor estiver ativo."}
            </div>
          </div>
        </SlideOverPanel>
      )}
    </PageContainer>
  );
}
